import { Button } from "@octopusdeploy/design-system-components";
import { useInlineStatusQuery, useMutation } from "@octopusdeploy/octopus-react-client";
import { CommunicationStyleValues } from "@octopusdeploy/octopus-server-client";
import type { KubernetesTentacleEndpointResource, MachineResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useState } from "react";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PagedOnboardingDialog } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import { createRemoveMonitorHelmCommand } from "../createHelmCommand";
import { UninstallKubernetesMonitorPage } from "./UninstallKubernetesMonitorPage";
import { getKubernetesMonitorDialogErrorPage } from "./kubernetesMonitorDialogErrorPage";
export interface UninstallKubernetesMonitorDialogProps {
    open: boolean;
    closeDialog: () => void;
    machine: MachineResource;
}
export function UninstallKubernetesMonitorDialog({ open, closeDialog, machine }: UninstallKubernetesMonitorDialogProps) {
    const [agentSelection, setAgentSelection] = useState<string | undefined>(machine.Name);
    const { navigate } = useSpaceAwareNavigation();
    const { result: agents, error } = useInlineStatusQuery(async (repository) => {
        const agents = await repository.Machines.list({ deploymentTargetTypes: CommunicationStyleValues.KubernetesTentacle });
        // Only auto select the first agent if we haven't explicitly provided one already
        if (!machine.Name && agents.Items.length > 0) {
            setAgentSelection(agents.Items[0].Name);
        }
        return agents.Items;
    }, [machine.Name], "KubernetesAgentTargetMonitorUpgradeDialog");
    const { execute: createHealthCheckTask, isExecuting: isCreatingHealthCheckTask } = useMutation({
        name: "Create health check task",
        action: (repo) => repo.Tasks.createHealthCheckTaskForMachine(machine),
        afterComplete: async (_, createdTask) => navigate(links.taskPage.generateUrl({ taskId: createdTask.Id })),
    });
    const selectedAgent = agents?.find((agent) => agent.Name === agentSelection);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const selectedAgentDetails = (selectedAgent?.Endpoint as KubernetesTentacleEndpointResource)?.KubernetesAgentDetails ?? null;
    if (error) {
        return <PagedOnboardingDialog open={open} close={closeDialog} pages={[getKubernetesMonitorDialogErrorPage(error)]} name={"Install Kubernetes monitor"} showPageIndicator={false} hidePreviousButton={false} hideCompleteButton={false}/>;
    }
    const page = {
        name: "Uninstall the Kubernetes monitor",
        title: "Uninstall the Kubernetes monitor",
        helpPanelHidden: true,
        render: () => (<UninstallKubernetesMonitorPage generateHelmCommand={(selectedLanguage) => createRemoveMonitorHelmCommand(selectedLanguage, selectedAgentDetails?.HelmReleaseName || "not-found", selectedAgentDetails?.KubernetesNamespace || "not-found")}/>),
        alwaysShowCloseButton: true,
        customPrimaryButton: <Button size="medium" importance="primary" onClick={createHealthCheckTask} label="Run health check" disabled={isCreatingHealthCheckTask}/>,
    };
    return <PagedOnboardingDialog open={open} close={closeDialog} pages={[page]} name={"Install Kubernetes monitor"} showPageIndicator={false} hidePreviousButton={false} hideCompleteButton={false}/>;
}
